import type {ReactNode} from "react";

import {cn} from "@/lib/utils";
import {formatDate, formatTime} from "@/src/modules/core/lib/formatters";
import {getVehicleIdentityTitle} from "@/src/modules/vehicles/lib/vehicle-identity";

import {
  formatCitationTowCompanyLine,
  getCitationTowDisplay,
  type CitationTowDisplay,
} from "../lib/get-citation-tow-display";
import {
  getCitationProofSectionTitle,
  getCitationTowDispatchFiles,
  getCitationTowProofSectionTitle,
} from "../lib/get-citation-tow-dispatch-files";
import {
  getCitationStoredVehicleBadgeLabel,
  getCitationStoredVehicleId,
  getCitationStoredVehicleSummary,
} from "../lib/citation-stored-vehicle-origin";
import {CitationPrintPhotoGrid} from "./citation-print-photo-grid";
import type {Citation, CitationFile, CitationTypeEmbedded} from "../types";

function typeLabel(t: CitationTypeEmbedded): string | null {
  const label = t.name?.trim() || t.description?.trim() || t.type?.trim();
  return label || null;
}

function PrintField({
  label,
  children,
  className,
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("min-w-0", className)}>
      <p className="text-[10px] font-semibold tracking-wide text-neutral-500 uppercase">{label}</p>
      <div className="mt-0.5 text-sm leading-snug text-neutral-900">{children}</div>
    </div>
  );
}

function PrintPhotoSection({title, files}: {title: string; files: CitationFile[]}) {
  if (!files.length) return null;

  return (
    <section className="mb-5 break-inside-avoid">
      <h2 className="mb-2 text-xs font-semibold text-neutral-700">{title}</h2>
      <CitationPrintPhotoGrid files={files} />
    </section>
  );
}

function PrintTowSection({
  citation,
  towDisplay,
  towCompanyLine,
  towFiles,
}: {
  citation: Citation;
  towDisplay: CitationTowDisplay;
  towCompanyLine: string | null;
  towFiles: CitationFile[];
}) {
  if (!towDisplay.show) return null;

  return (
    <section className="mb-5 break-inside-avoid border-t border-neutral-300 pt-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-semibold text-neutral-900">Tow</h2>
        {towDisplay.badge.showWorkflow ? (
          <span className="rounded border border-neutral-300 bg-neutral-100 px-2 py-0.5 text-[10px] font-medium text-neutral-700">
            {towDisplay.badge.label}
          </span>
        ) : null}
      </div>
      <div className="mb-3 grid grid-cols-2 gap-x-6 gap-y-3">
        {towCompanyLine ? <PrintField label="Tow company">{towCompanyLine}</PrintField> : null}
        {towDisplay.dispatchDetails ? (
          <PrintField label="Tow dispatch notes" className="col-span-2">
            <span className="whitespace-pre-wrap">{towDisplay.dispatchDetails}</span>
          </PrintField>
        ) : null}
        {towDisplay.cancellationReason ? (
          <PrintField label="Cancellation reason" className="col-span-2">
            <span className="whitespace-pre-wrap">{towDisplay.cancellationReason}</span>
          </PrintField>
        ) : null}
      </div>
      <PrintPhotoSection
        title={getCitationTowProofSectionTitle(citation)}
        files={towFiles}
      />
    </section>
  );
}

type Props = {
  citation: Citation;
  now: Date;
};

export function CitationDetailPrintView({citation, now}: Props) {
  const typeItems = (citation.citation_types ?? []).filter((t) => typeLabel(t));
  const attachmentFiles = citation.files ?? [];
  const proofFiles = citation.citation_proof?.files ?? [];
  const towFiles = getCitationTowDispatchFiles(citation);
  const towDisplay = getCitationTowDisplay(citation);
  const towCompanyLine = formatCitationTowCompanyLine(towDisplay.company, towDisplay.telephone);
  const storedVehicleId = getCitationStoredVehicleId(citation);
  const storedVehicleSummary = getCitationStoredVehicleSummary(citation);
  const reportedBy =
    citation.issued_by?.name?.trim() ||
    citation.issued_by?.user_name?.trim() ||
    (citation.issued_by ? `User #${citation.issued_by.user_id}` : "—");

  return (
    <article className="bg-white px-1 py-2 font-sans text-neutral-900">
      <header className="mb-5 border-b border-neutral-300 pb-3">
        <h1 className="text-lg font-semibold tracking-tight">Citation #{citation.citation_id}</h1>
        {citation.citation_date ? (
          <p className="mt-1 text-sm text-neutral-600 tabular-nums">
            {formatDate(citation.citation_date)} · {formatTime(citation.citation_date)}
          </p>
        ) : null}
      </header>

      <div className="mb-6 grid grid-cols-2 gap-x-6 gap-y-3">
        <PrintField label="Property">{citation.property.name}</PrintField>
        <PrintField label="Vehicle">
          <span className="font-mono tabular-nums">
            {getVehicleIdentityTitle(citation.vehicle)}
          </span>
          {(citation.vehicle.make || citation.vehicle.model) && (
            <span className="mt-0.5 block text-xs text-neutral-600">
              {[citation.vehicle.make, citation.vehicle.model].filter(Boolean).join(" · ")}
            </span>
          )}
        </PrintField>
        <PrintField label="Reported by">{reportedBy}</PrintField>
        {citation.household?.name?.trim() ? (
          <PrintField label="Household">{citation.household.name}</PrintField>
        ) : null}
        {typeItems.length > 0 ? (
          <PrintField label="Types" className="col-span-2">
            {typeItems.map((t) => typeLabel(t)).join(" · ")}
          </PrintField>
        ) : null}
        {citation.details?.trim() ? (
          <PrintField label="Details" className="col-span-2">
            <span className="whitespace-pre-wrap">{citation.details.trim()}</span>
          </PrintField>
        ) : null}
      </div>

      <PrintPhotoSection title="Attachments" files={attachmentFiles} />
      <PrintPhotoSection title={getCitationProofSectionTitle(citation)} files={proofFiles} />

      {storedVehicleId != null ? (
        <section className="mb-5 break-inside-avoid border-t border-neutral-300 pt-4">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <h2 className="text-sm font-semibold text-neutral-900">Stored vehicle</h2>
            <span className="rounded border border-neutral-300 bg-neutral-100 px-2 py-0.5 text-[10px] font-medium text-neutral-700">
              Issued from tracking
            </span>
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            <PrintField label="Record">
              {getCitationStoredVehicleBadgeLabel(storedVehicleId)}
            </PrintField>
            {storedVehicleSummary?.tracking_started_at?.trim() ? (
              <PrintField label="Tracking started">
                {formatDate(storedVehicleSummary.tracking_started_at)}{" "}
                {formatTime(storedVehicleSummary.tracking_started_at)}
              </PrintField>
            ) : null}
            {storedVehicleSummary?.cite_eligible_at?.trim() ? (
              <PrintField label="Cite eligible">
                {formatDate(storedVehicleSummary.cite_eligible_at)}{" "}
                {formatTime(storedVehicleSummary.cite_eligible_at)}
              </PrintField>
            ) : null}
          </div>
        </section>
      ) : null}

      <PrintTowSection
        citation={citation}
        towDisplay={towDisplay}
        towCompanyLine={towCompanyLine}
        towFiles={towFiles}
      />

      <footer className="mt-6 border-t border-neutral-300 pt-3 text-[10px] text-neutral-500">
        Created {formatDate(citation.created_at)} {formatTime(citation.created_at)} · Generated{" "}
        {formatDate(now)} {formatTime(now)}
      </footer>
    </article>
  );
}
